import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import AiChatAsync from 'store/aiChat/aiChatAsync';
import { aiChatActions } from 'store/aiChat/aiChatSlice';
import { selectTranslations } from 'store/aiChat/aiChatSelectors';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import { selectAiProvider } from 'store/currentUser/currentUserSelectors';
import IQuestion from 'models/Question';
import IBucket from 'models/Bucket';
import IScoringResultBand from 'models/ScoringResultBand';
import IPostQuizPage from 'models/PostQuizPage';
import IPage from 'models/Page';
import IBanner from 'models/Banner';
import ITimer from 'models/Timer';
import IAnswer from 'models/Answer';
import IRegistrationField, { IRegistrationFieldChoice } from 'models/RegistrationField';
import QuizTypes from 'types/QuizTypes';
import RegistrationFieldBindings from 'types/RegistrationFieldBindings';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Button, IconButton } from 'components/Buttons';
import { countries } from 'components/LanguageSelect';
import { Input } from 'components/Controls';
// MUI
import { Tooltip } from '@material-ui/core';
// Icons
import { FaTrash, FaEdit, FaCheck } from 'react-icons/fa';
// Utilities
import { aiProviderLabel } from 'utilities/utilities';
import QuestionTypes from 'types/QuestionTypes';

type Props = {
  open: boolean;
  onClose: () => void;
  language: string;
}

const GenerateTranslationsDialog:React.FC<Props> = ({ open, onClose, language }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const fields = useMemo(() => [
    { label: t('dialogs.generateTranslations.labels.brandName'), key: 'brand' },
    { label: t('dialogs.generateTranslations.labels.registrationCaption'), key: 'registrationPage.caption' },
    { label: t('dialogs.generateTranslations.labels.homeButtonTitle'), key: 'landingPage.buttonTitle' },
    { label: t('dialogs.generateTranslations.labels.homeTitle', { index: 1 }), key: 'landingPage.title1' },
    { label: t('dialogs.generateTranslations.labels.homeTitle', { index: 2 }), key: 'landingPage.title2' },
    { label: t('dialogs.generateTranslations.labels.homeTitle', { index: 3 }), key: 'landingPage.title3' },
    { label: t('dialogs.generateTranslations.labels.waiverText'), key: 'waiver.text' },
    { label: t('dialogs.generateTranslations.labels.countdownTitle'), key: 'countdownPage.title' },
    { label: t('dialogs.generateTranslations.labels.countdownText', { index: 1 }), key: 'countdownPage.text1' },
    { label: t('dialogs.generateTranslations.labels.countdownText', { index: 2 }), key: 'countdownPage.text2' },
  ], [t]);

  const { watch, getValues, setValue } = useFormContext();

  const lng = watch('language');

  // with useMemo watchedTranslations is not updated after using setValue
  // eslint-disable-next-line
  const watchedTranslations = watch('translations') || {};

  const quiz = useAppSelector(selectQuiz);
  const translations = useAppSelector(selectTranslations);
  const aiProvider = useAppSelector(selectAiProvider);

  const [isLoading, setIsLoading] = useState(false);
  const [initialTranslations, setInitialTranslations] = useState<any>({ [lng]: {} });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedKeyValue, setSelectedKeyValue] = useState<string>('');

  const isScoringQuizzes = quiz && [QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type);
  const isPollQuizzes = quiz && [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type);

  const getInitialTranslations = () => {
    const translations:Record<string, any> = { [lng]: {} };

    if (isScoringQuizzes || isPollQuizzes) {
      const ctaButtonLabel = isPollQuizzes ? t('dialogs.generateTranslations.labels.resultButtonTitle') : t('dialogs.generateTranslations.labels.leaderboardButtonTitle');
      fields.push(...[
        { label: t('dialogs.generateTranslations.labels.ctaTitle'), key: 'callToAction.title' },
        { label: ctaButtonLabel, key: 'callToAction.resultButtonTitle' },
        { label: t('dialogs.generateTranslations.labels.ctaText', { number: '' }), key: 'callToAction.text' },
        { label: t('dialogs.generateTranslations.labels.ctaText', { number: '2' }), key: 'callToAction.text2' },
        { label: t('dialogs.generateTranslations.labels.liveLeaderboardHeaderText'), key: 'publicLeaderboard.headerText' },
        { label: t('dialogs.generateTranslations.labels.liveLeaderboardQrCodeTitle'), key: 'publicLeaderboard.quizQRCodeTitle' },
      ]);

      (getValues('scoringResultPage.bands') || []).forEach((band: IScoringResultBand, index: number) => {
        const titleKey = `scoringResultPage.bands[${band.ref}].title`;
        translations[lng] = { ...translations[lng], [titleKey]: band.title };
        fields.push({ label: t('dialogs.generateTranslations.labels.scoreBandTitle', { number: index + 1 }), key: titleKey });

        if (band.text) {
          const textKey = `scoringResultPage.bands[${band.ref}].text`;
          translations[lng] = { ...translations[lng], [textKey]: band.text };
          fields.push({ label: t('dialogs.generateTranslations.labels.scoreBandText', { number: index + 1 }), key: textKey });
        }

        if (band.ctaButtonTitle) {
          const ctaButtonTitleKey = `scoringResultPage.bands[${band.ref}].ctaButtonTitle`;
          translations[lng] = { ...translations[lng], [ctaButtonTitleKey]: band.ctaButtonTitle };
          fields.push({ label: t('dialogs.generateTranslations.labels.scoreBandCtaButtonTitle', { number: index + 1 }), key: ctaButtonTitleKey });
        }
      });
    }

    if (quiz?.type === QuizTypes.Survey) {
      fields.push({ label: t('dialogs.generateTranslations.labels.ctaText', { number: '' }), key: 'callToAction.text' });
      fields.push({ label: t('dialogs.generateTranslations.labels.ctaTitle'), key: 'callToAction.title' });
    }

    if (quiz?.type === QuizTypes.Personality) {
      getValues('buckets').forEach((bucket: IBucket, index: number) => {
        const key = `buckets[${bucket.ref}].title`;
        translations[lng] = { ...translations[lng], [key]: bucket.title };
        fields.push({ label: `${t('dialogs.generateTranslations.labels.bucket')} ${index + 1}`, key });
      })

      getValues('postQuizPages').forEach((postQuizPage: IPostQuizPage, index: number) => {
        const buttonTitleKey = `postQuizPages[${index}].callToAction.buttonTitle`;
        const textKey = `postQuizPages[${index}].callToAction.text`;

        translations[lng] = { ...translations[lng], [buttonTitleKey]: postQuizPage.callToAction.buttonTitle };
        fields.push({ label: t('dialogs.generateTranslations.labels.ctaButtonTitle', { number: `${index + 1}` }), key: buttonTitleKey });

        translations[lng] = { ...translations[lng], [textKey]: postQuizPage.callToAction.text };
        fields.push({ label: t('dialogs.generateTranslations.labels.ctaText', { number: `${index + 1}` }), key: textKey });
      })
    }
  
    fields.forEach(field => {
      if (!getValues(field.key)) return;
      translations[lng] = { ...translations[lng], [field.key]: getValues(field.key) };
    })

    const spotPrizes = getValues('spotPrizes');
    if (spotPrizes) {
      translations[lng] = { ...translations[lng], spotPrizes };
      fields.push({ label: t('labels.randomPrize'), key: 'spotPrizes' })
    }

    getValues('prizes').forEach((prize:any, index: number) => {
      const key = `prizes.${index}.prize`;
      translations[lng] = { ...translations[lng], [key]: prize.prize };
      fields.push({ label: `${t('labels.prize')} ${index + 1}`, key });
    })

    getValues('questions').forEach((question: IQuestion, index: number) => {
      const key = `questions.${question.ref}.title`;
      translations[lng] = { ...translations[lng], [key]: question.title };
      fields.push({ label: `${t('labels.question')} ${index + 1}`, key });

      question.answers?.forEach((answer, i) => {
        const key = `questions[${question.ref}].answers.${answer.ref}.title`;
        translations[lng] = { ...translations[lng], [key]: answer.title };
        fields.push({ label: `${t('labels.answer')} ${i + 1}`, key });
      })

      if (quiz?.type === QuizTypes.Scoring && question.correctAnswerDescription) {
        const key = `questions.${question.ref}.correctAnswerDescription`;
        translations[lng] = { ...translations[lng], [key]: question.correctAnswerDescription };
        fields.push({ label: `${t('labels.question')} ${index + 1} - ${t('dialogs.generateTranslations.labels.additionalInfo')}`, key });
      }

      if (question.config?.clue) {
        const key = `questions.${question.ref}.config.clue`;
        translations[lng] = { ...translations[lng], [key]: question.config.clue };
        fields.push({ label: `${t('labels.question')} ${index + 1} - ${t('labels.clue')}`, key });
      }

      if (question.type === QuestionTypes.ConnectionGrid) {
        question.config?.groups?.forEach((group, groupIndex) => {
          const key = `questions.${question.ref}.config.groups.${groupIndex}.title`;
          translations[lng] = { ...translations[lng], [key]: group.title };
          fields.push({ label: `${t('labels.question')} ${index + 1} - ${t('labels.group')} ${groupIndex + 1} ${t('labels.title')}`, key });
        })
      }
    })

    const answersOptions = getValues('questionsSettings')?.answersOptions || [];

    if (answersOptions.length > 0) {
      answersOptions.forEach((answer:IAnswer, index: number) => {
        const key = `questionsSettings.answersOptions.${answer.ref}.title`;
        translations[lng] = { ...translations[lng], [key]: answer.title };
        fields.push({ label: `${t('labels.answer')} ${index + 1}`, key });
      });
    }

    getValues('pages').forEach((page: IPage, index: number) => {
      if (!page.title) return;
      const key = `pages.${page.uuid}.title`;
      translations[lng] = { ...translations[lng], [key]: page.title };
      fields.push({ label: `${t('labels.page')} ${index + 1} ${t('labels.title')}`, key });
    })

    getValues('banners').forEach((banner: IBanner, index: number) => {
      if (!banner.title) return;
      const key = `banners.${index}.title`;
      translations[lng] = { ...translations[lng], [key]: banner.title };
      fields.push({ label: `${t('labels.interstitial')} ${index + 1} ${t('labels.title')}`, key });
    })

    getValues('timers').forEach((timer: ITimer, index: number) => {
      if (!timer.title) return;
      const key = `timers.${index}.title`;
      translations[lng] = { ...translations[lng], [key]: timer.title };
      fields.push({ label: `${t('labels.timer')} ${index + 1} ${t('labels.title')}`, key });
    })

    const registrationFields = getValues('registrationPage.fields') || [];
    let customFieldNumber = 0;
    registrationFields.forEach((field: IRegistrationField) => {
      const isTeamField = field.binding === RegistrationFieldBindings.Team;
      if (!isTeamField) customFieldNumber += 1;

      if (field.title) {
        const key = `registrationPage.fields.${field.ref}.title`;
        translations[lng] = { ...translations[lng], [key]: field.title };
        fields.push({
          label: isTeamField
            ? t('dialogs.generateTranslations.labels.teamSelectorTitle')
            : t('dialogs.generateTranslations.labels.customField', { number: customFieldNumber }),
          key,
        });
      }

      field.choices?.forEach((choice: IRegistrationFieldChoice, choiceIndex: number) => {
        if (!choice.title) return;
        const choiceKey = `registrationPage.fields.${field.ref}.choices.${choice.ref}.title`;
        translations[lng] = { ...translations[lng], [choiceKey]: choice.title };
        fields.push({
          label: isTeamField
            ? t('dialogs.generateTranslations.labels.teamSelectorChoice', { number: choiceIndex + 1 })
            : t('dialogs.generateTranslations.labels.customFieldChoice', { fieldNumber: customFieldNumber, number: choiceIndex + 1 }),
          key: choiceKey,
        });
      });
    })

    setInitialTranslations(translations);
  }

  const nextTranslations = useMemo(() => {
    const result:any = { [lng]: {} };

    Object.keys(initialTranslations[lng]).filter(key => !watchedTranslations[language]?.[key]).forEach(field => {
      result[lng][field] = initialTranslations[lng][field]
    })

    return result;
  }, [initialTranslations, language, lng, watchedTranslations]);
  
  const handleApply = () => {
    setValue('translations', {
      ...watchedTranslations,
      [language]: {
        ...(watchedTranslations[language] || {}),
        ...(translations?.[language] || {}),
      }
    })
    onClose();
  }

  const handleTranslate = () => {
    setIsLoading(true);
    dispatch(AiChatAsync.generateTranslations({ language, translations: nextTranslations })).unwrap()
      .finally(() => setIsLoading(false));
  }

  const handleRemove = (key: string) => {
    setInitialTranslations((prev:any) => {
      const { [key]:_, ...otherTranslations } = prev[lng]
      return ({ ...prev, [lng]: otherTranslations });
    });
  }

  const handleEdit = (field:string) => {
    const value = translations?.[language]?.[field] ? translations[language][field] : watchedTranslations?.[language]?.[field] || '';
    setSelectedKey(field);
    setSelectedKeyValue(value);
  }

  const handleSave = (field: string) => {
    if (!selectedKey) return;

    if (translations?.[language]?.[field] !== undefined) {
      dispatch(aiChatActions.changeTranslationField({ lng: language, field, value: selectedKeyValue }));
    } else {
      setValue('translations', {
        ...watchedTranslations,
        [language]: {
          ...(watchedTranslations[language] || {}),
          [field]: selectedKeyValue
        }
      })
    }

    setSelectedKey(null);
    setSelectedKeyValue('');
  }

  useEffect(() => {
    getInitialTranslations();
    
    return () => {
      dispatch(aiChatActions.setInitialField('translations'));
    }
    // eslint-disable-next-line
  }, []);

  const isEmptyToTranslate = Object.keys(nextTranslations[lng]).length === 0;

  return (
    <Dialog size="xl" open={open} onClose={onClose}>
      <div className="flex flex-grow flex-col overflow-y-auto">
        <DialogTitle
          title={t('dialogs.generateTranslations.title')}
          onClose={onClose}
        >
          <span className="block text-gray-400 text-sm font-light leading-5">
            {t('dialogs.generateTranslations.hint', { language: countries.find(c => c.value === language)?.label || language })}
          </span>
        </DialogTitle>
        <DialogContent>
          <table>
            <thead>
              <tr>
                <th className="text-left">{t('labels.label')}</th>
                <th className="text-left">{t('labels.value')}</th>
                <th className="text-left">{t('labels.translation')}</th>
                <th className="text-right">{t('labels.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(initialTranslations[lng]).map(field => (
                <tr key={field}>
                  <td className="text-left title w-3/12">{fields.find(f => f.key === field)?.label || ''}</td>
                  <td className="text-left title w-4/12">{initialTranslations[lng][field]}</td>
                  <td className="text-left title w-4/12" onDoubleClick={() => handleEdit(field)}>
                    {selectedKey === field ? (
                      <Input
                        id="edit-translation-input" name="edit-translation-input"
                        className="mb-0"
                        value={selectedKeyValue}
                        onChange={(e) => setSelectedKeyValue(e.target.value)}
                        onKeyPress={e => {
                          if (e.key === 'Enter') handleSave(field);
                        }}
                      />
                    ) : (
                      <span>
                        {watchedTranslations[language]?.[field]
                          ? watchedTranslations[language][field]
                            : translations?.[language]?.[field]
                            ? translations[language][field]
                          : ''}
                      </span>
                    )}
                  </td>
                  <td className="actions flex items-center justify-end">
                    {!translations && !watchedTranslations[language]?.[field] && (
                      <Tooltip className="ml-1" title={t('buttons.remove')}>
                        <div>
                          <IconButton
                            onClick={() => handleRemove(field)}
                          ><FaTrash /></IconButton>
                        </div>
                      </Tooltip>
                    )}
                    <Fragment>
                      {selectedKey === field ? (
                        <Tooltip className="ml-1" title={t('buttons.save')}>
                          <div>
                            <IconButton
                              onClick={() => handleSave(field)}
                            ><FaCheck /></IconButton>
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip className="ml-1" title={t('buttons.edit')}>
                          <div>
                            <IconButton
                              onClick={() => handleEdit(field)}
                            ><FaEdit /></IconButton>
                          </div>
                        </Tooltip>
                      )}
                    </Fragment>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions>
          {aiProvider ? (
            <span className="block text-gray-400 text-sm font-light leading-5">{`Powered by ${aiProviderLabel[aiProvider]}`}</span>
          ) : null}
          <div className="flex-grow" />
          <Button
            onClick={onClose}
          >{t('buttons.close')}</Button>
          <Button
            className="ml-2"
            variant="outlined"
            loading={isLoading}
            onClick={handleTranslate}
            disabled={isEmptyToTranslate}
          >{t('dialogs.generateTranslations.translateButton')}</Button>
          <Button
            className="ml-2"
            variant="contained"
            onClick={handleApply}
            disabled={isLoading}
          >{t('buttons.apply')}</Button>
        </DialogActions>
      </div>
    </Dialog>
  )
}

export default GenerateTranslationsDialog;
